import type { ReactNode } from 'react';

interface SettingsPageShellProps {
  /** Eyebrow line above the title — e.g. "Settings · Bonsai Bar". */
  crumb: string;
  /** Page title rendered in the headline face. */
  title: string;
  /** Optional right-aligned action area (buttons, filters, etc.). */
  actions?: ReactNode;
  /** Page body. The shell handles padding + scroll. */
  children: ReactNode;
}

/** Consistent layout wrapper for every Settings sub-page. Provides the
 *  breadcrumb + headline-font title at the top and uniform padding around
 *  the body, so each sub-page (Users, Audit, Tree Species, etc.) lines up
 *  visually under the Settings sidebar. */
export default function SettingsPageShell({
  crumb,
  title,
  actions,
  children,
}: SettingsPageShellProps) {
  return (
    <div className="flex flex-col gap-3.5 h-full min-h-0 px-6 py-5">
      <div className="flex items-end gap-4">
        <div>
          <div className="text-[10px] font-semibold tracking-[.08em] uppercase text-stone-500 dark:text-stone-400 mb-1">
            {crumb}
          </div>
          <h1 className="font-headline text-4xl tracking-tight text-[#173404] dark:text-[#e9efe9] m-0 leading-none">
            {title}
          </h1>
        </div>
        {actions && (
          <>
            <div className="flex-1" />
            <div className="flex items-center gap-2">{actions}</div>
          </>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col gap-3.5">{children}</div>
    </div>
  );
}
